import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../assets/Logo.png';
import AuthImage from '../../assets/New_petrotago.jfif';
import { resetPassword, resendCode, ApiError, PasswordResetError } from '../../utils/auth';
import { useCountdown } from '../../hooks/useCountdown';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? '');
  const [formData, setFormData] = useState({ code: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  // One emailed code per 60 s; a 429 carries retryAfter for the countdown.
  const resendWait = useCountdown();
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'error' | 'success' }>({
    open: false, message: '', severity: 'error',
  });
  const showToast = (message: string, severity: 'error' | 'success' = 'error') =>
    setToast({ open: true, message, severity });
  const closeToast = () => setToast(prev => ({ ...prev, open: false }));

  const handleSubmit = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showToast('Please enter a valid email address');
      return;
    }
    if (!formData.code.trim()) {
      showToast('Enter the code we emailed you');
      return;
    }
    if (!formData.password || !formData.confirmPassword) {
      showToast('Please fill in all fields');
      return;
    }
    if (formData.password.length < 8) {
      showToast('Password must be at least 8 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      showToast('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, formData.code.trim(), formData.password, formData.confirmPassword);
      setSuccess(true);
    } catch (err) {
      const apiErr = err as PasswordResetError;
      const remaining = apiErr?.attemptsRemaining ?? apiErr?.trialsRemaining;
      const suffix = remaining != null ? ` (${remaining} attempt${remaining === 1 ? '' : 's'} remaining)` : '';
      showToast((apiErr?.message ?? 'Failed to reset password.') + suffix);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showToast('Enter your email address first');
      return;
    }
    if (resendWait.remaining > 0) return;
    setResending(true);
    try {
      await resendCode(email, 'password_reset');
      resendWait.start(60);
      showToast(`If ${email} has an account, we've sent it a new code.`, 'success');
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 429) resendWait.start(apiErr.retryAfter ?? 60);
      showToast(apiErr?.message ?? 'Unable to reach the server. Please check your connection.');
    } finally {
      setResending(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#FFFFFF',
      fontSize: '0.925rem',
      height: '48px',
      gap: '24px',
      opacity: 1,
      transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      '& fieldset': { borderColor: 'rgba(217, 217, 217, 1)', borderWidth: '1px' },
      '&:hover fieldset': { borderColor: 'rgba(180, 195, 215, 1)', borderWidth: '1px' },
      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(43,103,176,0.14)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(43,103,176,1) !important', borderWidth: '1.5px !important' },
      '& input': { padding: '13px 14px' },
      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
        WebkitBoxShadow: '0 0 0 100px #ffffff inset',
        WebkitTextFillColor: '#000000',
        caretColor: '#000000',
      },
    },
  };

  const labelSx = {
    fontFamily: '"Montserrat", sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: '0%',
    color: 'rgba(0, 0, 0, 1)',
    mb: 0.5,
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' }, overflow: 'hidden' }}>

      {/* ── LEFT PANEL ─────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 100% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(circle 420px at 4% 8%, rgba(43,103,176,0.18), rgba(43,103,176,0) 75%),
            radial-gradient(circle 500px at 8% 98%, rgba(30,58,95,0.16), rgba(30,58,95,0) 75%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, auto, auto, 26px 26px',
          backgroundPosition: 'right center, 0 0, 0 0, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat, no-repeat, repeat',
          height: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          py: { xs: '20px', sm: '32px', md: '48px' },
          px: { xs: 2, sm: 3 },
        }}
      >
      <Box
        sx={{
          my: 'auto',
          width: { xs: '100%', sm: '440px', md: '452px' },
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 2.5, sm: 4, md: 5 },
          bgcolor: '#ffffff',
          borderRadius: '22px',
          border: '1px solid rgba(226,232,240,0.7)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.03), 0 10px 22px -14px rgba(30,58,95,0.12)',
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4 }}>
          <Box
            component="img"
            src={Logo}
            alt="PetroTag360"
            sx={{ height: 80, objectFit: 'contain', objectPosition: 'left' }}
          />
        </Box>

        {success ? (
          /* ── Success state ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mt: 4 }}>
            <CheckCircleOutline sx={{ fontSize: 64, color: 'rgba(43, 103, 176, 1)', mb: 2 }} />
            <Typography
              sx={{
                fontFamily: '"Unbounded", sans-serif',
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '40px',
                color: 'rgba(0, 0, 0, 1)',
                opacity: 0.8,
                mb: 1.5,
              }}
            >
              Password Reset!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '140%',
                color: 'rgba(87, 81, 81, 1)',
                mb: 4,
              }}
            >
              Your password has been successfully reset. You can now log in with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                height: '48px',
                background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
                borderRadius: '11px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textTransform: 'none',
                letterSpacing: 0.3,
                boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
                transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                  boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Back to Login
            </Button>
          </Box>
        ) : (
          <>
            {/* Heading */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.25 }}>
                <Box sx={{ width: 26, height: 3, borderRadius: 2, background: 'linear-gradient(90deg, rgba(43,103,176,1), rgba(27,72,132,1))' }} />
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(43,103,176,1)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Account Recovery
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: '"Unbounded", sans-serif',
                  fontWeight: 700,
                  fontSize: '33.31px',
                  lineHeight: '48px',
                  letterSpacing: '0%',
                  color: '#0F1D30',
                  mb: 1,
                }}
              >
                Reset Password
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Montserrat", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: 1.5,
                  letterSpacing: '0%',
                  color: 'rgba(87, 81, 81, 1)',
                }}
              >
                Enter the code we emailed you and choose a new password.
              </Typography>
            </Box>

            <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

              {/* Email */}
              <Typography sx={labelSx}>Email address</Typography>
              <TextField
                fullWidth
                placeholder="Enter your email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                size="small"
                sx={{ ...inputSx, mb: 2 }}
              />

              {/* Reset code */}
              <Typography sx={labelSx}>Reset code</Typography>
              <TextField
                fullWidth
                placeholder="Enter code"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                disabled={loading}
                size="small"
                inputProps={{ maxLength: 10, inputMode: 'numeric', autoComplete: 'one-time-code' }}
                sx={{ ...inputSx, mb: 1 }}
              />
              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  underline="hover"
                  sx={{
                    fontFamily: '"Montserrat", sans-serif', fontWeight: 500, fontSize: '12.5px',
                    color: resending || resendWait.remaining > 0 ? 'rgba(0,0,0,0.4)' : 'rgba(43, 103, 176, 1)',
                    pointerEvents: resending || resendWait.remaining > 0 ? 'none' : 'auto',
                  }}
                >
                  {resending ? 'Sending…' : resendWait.remaining > 0 ? `Resend code in ${resendWait.remaining}s` : 'Resend code'}
                </Link>
              </Box>

              {/* New Password */}
              <Typography sx={labelSx}>New Password</Typography>
              <TextField
                fullWidth
                placeholder="Enter new password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                disabled={loading}
                size="small"
                sx={{ ...inputSx, mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Confirm Password */}
              <Typography sx={labelSx}>Confirm Password</Typography>
              <TextField
                fullWidth
                placeholder="Confirm new password"
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                disabled={loading}
                size="small"
                sx={{ ...inputSx, mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Submit button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  height: '48px',
                  background: 'linear-gradient(135deg, rgba(51,115,190,1) 0%, rgba(24,64,118,1) 100%)',
                  borderRadius: '11px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  letterSpacing: 0.3,
                  boxShadow: '0 12px 24px -8px rgba(30,74,138,0.55)',
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  mb: 2.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(41,100,168,1) 0%, rgba(18,52,98,1) 100%)',
                    boxShadow: '0 16px 30px -8px rgba(30,74,138,0.6)',
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': { background: '#93C5FD', boxShadow: 'none' },
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                    Resetting...
                  </Box>
                ) : 'Reset Password'}
              </Button>

              {/* Back to login */}
              <Box sx={{ textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/')}
                  underline="hover"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontFamily: '"Montserrat", sans-serif',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: 'rgba(43, 103, 176, 1)',
                  }}
                >
                  <ArrowBack sx={{ fontSize: '16px' }} />
                  Back to Login
                </Link>
              </Box>
            </Box>
          </>
        )}
      </Box>
      </Box>

      {/* ── RIGHT PANEL ────────────────────────────────────────────── */}
      <Box
        sx={{
          position: 'relative',
          display: { xs: 'none', lg: 'block' },
          width: 'min(45vw, calc(100vh * 1576 / 2048))',
          height: '100vh',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={AuthImage}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
        />
        {/* Continue the left panel's exact texture (color, dot-grid, glow) across the seam,
            masked to fade out — so the boundary reads as one continuous surface, not a cut */}
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundColor: '#F5F7FB',
          backgroundImage: `
            radial-gradient(ellipse 1050px 900px at 0% 40%, rgba(43,103,176,0.20), rgba(43,103,176,0) 78%),
            radial-gradient(rgba(30,58,95,0.16) 1px, transparent 1px)
          `,
          backgroundSize: 'auto, 26px 26px',
          backgroundPosition: 'left center, 0 0',
          backgroundRepeat: 'no-repeat, repeat',
          WebkitMaskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          maskImage: 'linear-gradient(90deg, black 0%, black 4%, transparent 26%)',
          pointerEvents: 'none',
        }} />
        {/* Deepen the shadows toward the brand navy so the photo reads darker and richer */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(200deg, rgba(15,29,48,0.32) 0%, rgba(15,29,48,0.05) 55%, rgba(15,29,48,0.22) 100%)',
          pointerEvents: 'none',
        }} />
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={closeToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '10px', fontFamily: '"Montserrat", sans-serif', fontSize: '0.85rem' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResetPasswordPage;
